import { type Node as PMNode, type Schema } from "prosemirror-model";
import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet, type EditorView } from "prosemirror-view";

interface AppendixState {
	headingPos: number | null;
	collapsed: boolean;
}

export const appendixPluginKey = new PluginKey<AppendixState>("appendix");

export function findAppendixHeading(
	doc: PMNode,
	schema: Schema
): { headingPos: number; headingSize: number } | null {
	let result: { headingPos: number; headingSize: number } | null = null;

	doc.forEach((node, offset) => {
		if (node.type === schema.nodes.heading && node.textContent.trim() === "Appendix") {
			result = { headingPos: offset, headingSize: node.nodeSize };
		}
	});

	return result;
}

function createToggleWidget(collapsed: boolean, view: EditorView): HTMLElement {
	const button = document.createElement("button");
	button.type = "button";
	button.className = "pm-appendix-toggle";
	button.contentEditable = "false";
	button.setAttribute("aria-expanded", String(!collapsed));
	button.setAttribute("data-collapsed", String(collapsed));
	button.textContent = "▶";
	button.addEventListener("mousedown", (e) => {
		e.preventDefault();
		e.stopPropagation();
		view.dispatch(
			view.state.tr
				.setMeta(appendixPluginKey, { toggle: true })
				.setMeta("addToHistory", false)
		);
	});
	return button;
}

export const appendixPlugin = (schema: Schema) =>
	new Plugin<AppendixState>({
		key: appendixPluginKey,

		state: {
			init(_, state): AppendixState {
				const result = findAppendixHeading(state.doc, schema);
				return {
					headingPos: result?.headingPos ?? null,
					collapsed: result !== null,
				};
			},
			apply(tr, value): AppendixState {
				const meta = tr.getMeta(appendixPluginKey);
				const result = findAppendixHeading(tr.doc, schema);
				const headingPos = result?.headingPos ?? null;

				if (meta?.toggle) {
					return { headingPos, collapsed: !value.collapsed };
				}

				// If the appendix heading disappeared, reset
				if (headingPos === null) {
					return { headingPos: null, collapsed: true };
				}

				// If the appendix heading just appeared, default collapsed
				if (value.headingPos === null && headingPos !== null) {
					return { headingPos, collapsed: true };
				}

				// Preserve collapsed state across normal transactions
				return { headingPos, collapsed: value.collapsed };
			},
		},

		props: {
			decorations(state) {
				const pluginState = appendixPluginKey.getState(state);
				if (!pluginState || pluginState.headingPos === null) {
					return DecorationSet.empty;
				}

				const { headingPos, collapsed } = pluginState;
				const decos: Decoration[] = [];

				// Widget decoration for the disclosure triangle inside the heading
				decos.push(
					Decoration.widget(
						headingPos + 1,
						(view) => createToggleWidget(collapsed, view),
						{
							side: -1,
							key: `appendix-toggle-${collapsed}`,
						}
					)
				);

				// Node decoration on the heading itself
				decos.push(
					Decoration.node(
						headingPos,
						headingPos + state.doc.nodeAt(headingPos)!.nodeSize,
						{
							"class": "pm-appendix-heading",
							"data-appendix-collapsed": String(collapsed),
						}
					)
				);

				// If collapsed, hide all top-level nodes after the heading
				if (collapsed) {
					const headingNode = state.doc.nodeAt(headingPos)!;
					const contentFrom = headingPos + headingNode.nodeSize;

					state.doc.forEach((node, offset) => {
						if (offset >= contentFrom) {
							decos.push(
								Decoration.node(offset, offset + node.nodeSize, {
									class: "pm-appendix-hidden",
								})
							);
						}
					});
				}

				return DecorationSet.create(state.doc, decos);
			},
		},

		// Auto-expand if the selection lands inside the collapsed appendix body
		appendTransaction(transactions, _oldState, newState) {
			// Don't fight a manual toggle that just happened
			if (transactions.some((tr) => tr.getMeta(appendixPluginKey)?.toggle)) {
				return null;
			}

			const pluginState = appendixPluginKey.getState(newState);
			if (!pluginState || !pluginState.collapsed || pluginState.headingPos === null) {
				return null;
			}

			const headingNode = newState.doc.nodeAt(pluginState.headingPos);
			if (!headingNode) return null;

			const contentFrom = pluginState.headingPos + headingNode.nodeSize;
			const { from, to } = newState.selection;

			// If selection is in the hidden content, expand the appendix
			if (from >= contentFrom || to >= contentFrom) {
				return newState.tr
					.setMeta(appendixPluginKey, { toggle: true })
					.setMeta("addToHistory", false);
			}

			return null;
		},
	});
